/**
 * Grok through xAI's OpenAI-compatible API with the user's own key. api.x.ai
 * answers CORS for any origin, so the browser calls it directly.
 */

import { ShaderGenerationError } from '../types'
import { getJson, postChatCompletion } from './openai-compatible'
import type { ChatTransportRequest, ShaderModelOption } from './openai-compatible'

export const XAI_API_URL = 'https://api.x.ai/v1'

/** Image, video and voice models share the listing; none of them writes code. */
const NON_TEXT_MODEL = /imagine|image|video|voice|embed|tts|audio/i

function unreachable(): ShaderGenerationError {
  return new ShaderGenerationError('Could not reach xAI. Check your connection and try again.', 'NETWORK')
}

export async function completeGrok(key: string | undefined, request: ChatTransportRequest): Promise<string> {
  if (!key) throw new ShaderGenerationError('Add your xAI API key to use Grok.', 'AUTH')
  return postChatCompletion(
    {
      url: `${XAI_API_URL}/chat/completions`,
      label: 'Grok',
      headers: { Authorization: `Bearer ${key}` },
      secret: key,
      onNetworkError: unreachable,
    },
    request,
  )
}

interface XaiModelsResponse {
  data?: Array<{ id?: string }>
}

/** The text models this key can use. */
export async function listGrokModels(key: string, signal?: AbortSignal): Promise<ShaderModelOption[]> {
  const body = await getJson<XaiModelsResponse>(
    { url: `${XAI_API_URL}/models`, label: 'Grok', headers: { Authorization: `Bearer ${key}` }, secret: key, onNetworkError: unreachable },
    signal,
  )
  return (body.data ?? [])
    .map((model) => model.id)
    .filter((id): id is string => typeof id === 'string' && !NON_TEXT_MODEL.test(id))
    .map((id) => ({ id, label: id }))
}
